// Adapters that turn raw `profiles` + `user_picks` rows into the
// `Recommender` / `Restaurant` shapes the UI components consume. Keeping
// the conversion in one place means consumers don't need to handle the
// snake_case-to-camelCase + position-to-rank reshaping themselves.

import type { Profile, Recommender, Restaurant, UserPick } from '../types';

export function mapsUrlForPick(pick: { name: string; location: string; placeId: string }): string {
  const query = `${pick.name} ${pick.location}`.trim();
  return (
    'https://www.google.com/maps/search/?api=1' +
    `&query=${encodeURIComponent(query)}` +
    `&query_place_id=${encodeURIComponent(pick.placeId)}`
  );
}

// Rank is the pick's index in the user's sorted list (1-based), not
// `position + 1` directly — the `position` column can be gappy after a
// remove + add cycle (e.g. [0, 1, 3, 4, 5]). Computing rank from the
// sorted index keeps the displayed ranks contiguous 1..5 even if the
// underlying positions aren't.
export function pickToRestaurant(pick: UserPick, displayRank: number): Restaurant {
  return {
    rank: displayRank,
    name: pick.name,
    cuisine: pick.cuisine,
    location: pick.location,
    googleMapsUrl: mapsUrlForPick(pick),
    placeId: pick.placeId,
  };
}

export function buildRecommender(profile: Profile, picks: UserPick[]): Recommender {
  const ordered = picks.slice().sort((a, b) => a.position - b.position);
  return {
    id: profile.userId,
    name: profile.displayName,
    slug: profile.slug,
    restaurants: ordered.map((pick, idx) => pickToRestaurant(pick, idx + 1)),
  };
}
